<template>
<div>
  <div class="row">
    <div v-if="currentMeetingView == 'internalMeeting'" class="container">
      <div v-if="!update && !isExtended && !busy">
        <h6>{{ $t('network.networkForm.have-a-meeting-link') }}
          <strong class="text-primary" role="button" @click="currentMeetingView = 'externalMeeting'">{{ $t('network.networkForm.insert-an-existing-meeting') }}</strong>
        </h6>
      </div>
      <div v-if="isExtended && !busy" class="mt-n3">
        <h6>Fill in the required details to extend occurrences for this meeting. Previous occurrences will be read-only.</h6>
      </div>
      <div v-if="!busy" class="card shadow-lg">
        <div class="alert alert-warning alert-dismissible fade show text-black fw-semibold mx-3 mt-3 mb-0" role="alert">
          <i class="fa-solid fa-triangle-exclamation fs-5 me-1"></i>
          <span>
            Please be advised that once a Zoom meeting has been created, any modifications or updates to the meeting details must be made within Networks.
            Making changes directly through Zoom will cause the meeting data to become out of sync with our records.
            We appreciate your cooperation in ensuring data accuracy.
          </span>
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveMeeting">
            <div class="row mt-2">
              <div class="col-sm-2">
                <strong>{{ $t('network.networkForm.topic') }}</strong>
              </div>
              <div class="col-sm-10">
                <b-form-input id="title" v-model="form.title" data-test="title" :placeholder="this.$t('network.networkForm.title')" class="mb-2" required></b-form-input>
                <b-form-textarea id="description" v-model="form.content" data-test="description" :placeholder="this.$t('network.networkForm.description')" rows="3" max-rows="6"></b-form-textarea>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-2">
                <strong>{{ $t('network.meeting.when') }}</strong>
              </div>
              <div class="col-sm-10 p-0">
                <div class="row">
                  <date-picker
                    id="start-datepicker"
                    ref="datepicker"
                    v-model="form.start_date"
                    data-test="start-datepicker"
                    :valueType="'format'"
                    required
                    style="width: 160px;"
                    @change="$refs.datepicker.value = form.start_date"
                  ></date-picker>
                  <date-picker
                    id="start_time"
                    v-model="form.start_time"
                    data-test="start_time"
                    format="hh:mm"
                    :hour-options="hours"
                    value-type="format"
                    type="time"
                    :placeholder="form.start_time"
                    locale="en"
                    required
                    style="width: 135px;"
                  ></date-picker>
                  <b-form-select
                    id="am_pm"
                    v-model="am_pm"
                    data-test="am_pm"
                    :options="['AM', 'PM']"
                    required
                    style="width: 77px; height: 32px; border-color: #ccc; text-align: center; margin: 1px 15px;"
                  ></b-form-select>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-2">
                <strong>{{ $t('network.meeting.duration') }}</strong>
              </div>
              <div class="col-sm-8 p-0">
                <div class="row">
                  <div class="col">
                  <b-form-group>
                    <b-form-select id="duration" v-model="form.duration" data-test="duration" :options="fiveHoursIn15MinutesInterval" required
                    style="width: 90px; height: 30px; border-radius: 1rem; border-color: rgb(192, 192, 192); text-align: center;"></b-form-select>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>

            <div class="row mt-4">
              <div class="col-sm-2">
                <strong>{{ $t('presenter') }}</strong>
              </div>
              <div class="col-sm-10">
                  <b-form-group>
                    <PresenterField :existingPresenters="form.presenter" @presenter-form-field-update="presenterUpdate" />
                  </b-form-group>
                </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-2">
                <strong>{{ $t('network.meeting.timezone') }}</strong>
              </div>
              <div class="col-sm-10">
                <v-select
                  id="timezones"
                  v-model="form.timezone"
                  data-test="timezones"
                  :options="timezones"
                  label="name"
                  :placeholder="$t('network.meeting.selectTimezone')"
                  :disabled="forOccurrence"
                  :clearable=false
                  style="max-width: 480px;"
                ></v-select>
              </div>
            </div>

            <div v-if="!forOccurrence" class="row mt-4">
              <div class="col-sm-2"></div>
              <div class="col-sm-10">
                <b-form-checkbox id="is_recurring" v-model="form.isRecurring" data-test="is_recurring" :disabled="!validateNonRecurringFields()" class="w-25 d-inline-block">
                  <strong>&nbsp;&nbsp;{{ $t('network.meeting.recurring') }}</strong>
                </b-form-checkbox>
              </div>
            </div>

            <div v-if="form.isRecurring && !forOccurrence" class="row mt-4">
              <div class="col-sm-2">
              </div>
              <div class="col-sm-10">
                <div class="row">
                  <div class="col-sm-2">
                    <strong>{{ $t('network.meeting.recurrence') }}</strong>
                  </div>
                  <div class="col-sm-8">
                    <b-form-select id="recurring_frequency" v-model="form.recurring.frequency" data-test="recurring_frequency" :options="['Daily', 'Weekly', 'Monthly']"
                    style="width: 95px; height: 30px; border-radius: 1rem; border-color: rgb(192, 192, 192); text-align: center;"></b-form-select>
                  </div>
                </div>
                <div class="row mt-4">
                  <div class="col-sm-2">
                    <strong>{{ $t('network.meeting.repeatEvery') }}</strong>
                  </div>
                  <div class="col-sm-8">
                    <b-form-select
                      id="recurring_repeat_every"
                      v-model="form.recurring.repeatEvery"
                      data-test="recurring_repeat_every"
                      :options="Array.from({ length: form.recurring.frequency === 'Daily' ? 15 : form.recurring.frequency === 'Weekly' ? 12 : 3 }, (_, i) => i + 1)"
                      class="timeInterval"
                    ></b-form-select>
                    &nbsp;&nbsp;{{ form.recurring.frequency === 'Daily' ? 'Days' : form.recurring.frequency === 'Weekly' ? 'Weeks' : 'Months' }}
                  </div>
                </div>
                <div v-if="form.recurring.frequency !== 'Daily'" class="row mt-4">
                  <div class="col-sm-2">
                    <strong>{{ $t('network.meeting.occursOn') }}</strong>
                  </div>
                  <div class="col-sm-8">
                    <div v-if="form.recurring.frequency === 'Weekly'">
                      <b-form-checkbox-group v-model="form.recurring.occurs_on_array" class="ml-2" @change="resetWeeklyOccursOn">
                        <b-form-checkbox v-for="day in daysOfWeek" id="day" :key="day.id" data-test="day" :value="day" class="w-10 d-inline-block">&nbsp;{{ day.text }}&nbsp;&nbsp;</b-form-checkbox>
                      </b-form-checkbox-group>
                    </div>
                    <div v-if="form.recurring.frequency === 'Monthly'">
                      <div class="form-check">
                        <label class="form-check-label fs-6">
                          <input
                            id="chosen_monthly_type_one"
                            v-model="selectedMonthlyOption"
                            data-test="chosen_monthly_type_one"
                            name="chosenMonthlyType"
                            type="radio"
                            value="type_one"
                            class="form-check-input"
                            style="width: 1.5em; height: 1.5em;"
                            :checked="'type_one'"
                          />
                          <span style="vertical-align: sub; margin-left: 10px;">
                            {{ $t('network.meeting.day') }}&nbsp;
                            <b-form-select id="recurring_occurs_on_type_one" v-model="form.recurring.occurs_on" data-test="recurring_occurs_on_type_one"
                            :options="Array.from({ length: 31 }, (_, i) => i + 1)" class="timeInterval"
                            :style="{'color': selectedMonthlyOption === 'type_two' ? 'white' : 'black'}"></b-form-select>
                            &nbsp;{{ $t('network.meeting.ofTheMonth') }}
                          </span>
                        </label>
                      </div><br/>
                      <div class="form-check">
                        <label class="form-check-label fs-6">
                          <input
                            id="chosen_monthly_type_two"
                            v-model="selectedMonthlyOption"
                            data-test="chosen_monthly_type_two"
                            name="chosenMonthlyType"
                            type="radio"
                            value="type_two"
                            class="form-check-input"
                            style="width: 1.5em ; height: 1.5em;"
                            :checked="'type_two'"
                          />
                          <span style="vertical-align: sub; margin-left: 10px;">
                            <b-form-select id="recurring_occurs_on_week" v-model="form.recurring.occurs_on_week" data-test="recurring_occurs_on_week" class="timeInterval"
                            :style="{'color': selectedMonthlyOption === 'type_one' ? 'white' : 'black'}" style="width: 125px;"
                            :options="[
                                { text: 'First', value: 1 },
                                { text: 'Second', value: 2 },
                                { text: 'Third', value: 3 },
                                { text: 'Fourth', value: 4 },
                                { text: 'Last', value: 5 }
                                ]">
                            </b-form-select>
                            &nbsp;
                            <b-form-select id="recurring_occurs_on_type_two" v-model="form.recurring.occurs_on" data-test="recurring_occurs_on_type_two" class="timeInterval"
                            :style="{'color': selectedMonthlyOption === 'type_one' ? 'white' : 'black'}" style="width: 125px;"
                            :options="[
                                { text: 'Sunday', value: 1 },
                                { text: 'Monday', value: 2 },
                                { text: 'Tuesday', value: 3 },
                                { text: 'Wednesday', value: 4 },
                                { text: 'Thursday', value: 5 },
                                { text: 'Friday', value: 6 },
                                { text: 'Saturday', value: 7 }
                              ]">
                            </b-form-select>
                            &nbsp;{{ $t('network.meeting.ofTheMonth') }}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mt-4">
                  <div class="col-sm-2">
                    <strong>{{ $t('network.meeting.endDate') }}</strong>
                  </div>
                  <div class="col-sm-8">
                    <date-picker
                      id="enddate-picker"
                      ref="datepicker"
                      v-model="form.recurring.endDate"
                      data-test="enddate-picker"
                      :valueType="'format'"
                      required
                      @change="$refs.datepicker.value = form.recurring.endDate"
                    ></date-picker>
                  </div>
                </div>
              </div>
              <div v-if="form.isRecurring && !forOccurrence" class="row mt-4">
                <div class="col-sm-2"></div>
                <div class="col-sm-10">
                  <b-form-checkbox id="auto_extend" v-model="form.auto_extend" data-test="auto_extend" class="d-inline-block">
                    <strong>&nbsp;&nbsp;Automatically extend after ending</strong>
                  </b-form-checkbox>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-2"></div>
              <div class="col-sm-10">
                <button id="save_meeting" data-test="save_meeting" type="submit" class="btn btn-primary mt-4">{{ $t('network.meeting.saveMeeting') }}</button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div v-else class="text-center">
        <lottie-player
          autoplay
          loop
	        speed="2.5"
          src="/img/animations/custom_loader.json"
          class="m-6"
          style="height: 200px;"
          >
        </lottie-player>
      </div>
    </div>
    <div v-else class="container">
      <div v-if="!update">
        <h6>{{ $t('network.meeting.dont-have-a-meeting-link') }}
          <strong role="button" class="text-primary" @click="currentMeetingView = 'internalMeeting'">{{ $t('network.meeting.schedule-meeting-and-add-required-details') }}</strong>
        </h6>
      </div>
      <div class="card shadow-lg">
        <div class="alert alert-warning alert-dismissible fade show text-black fw-semibold mx-3 mt-3 mb-0" role="alert">
          <i class="fa-solid fa-triangle-exclamation fs-5 me-1"></i>
          <span>
            Please be advised that once a Zoom meeting has been created, any modifications or updates to the meeting details must be made within Networks.
            Making changes directly through Zoom will cause the meeting data to become out of sync with our records.
            We appreciate your cooperation in ensuring data accuracy.
          </span>
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
        <div class="card-body">
          <form @submit.prevent="saveMeeting">
            <div class="row mt-2">
              <div class="col-sm-1 mt-2 p-0">
                <strong>{{ $t('network.meeting.meeting-id') }}</strong>
              </div>
              <div class="col-sm-11">
                  <b-form-input id="zoom_id" v-model="zoom_id" data-test="zoom_id" :placeholder="$t('enter-meeting-id-10-or-11-digit-number-e-g-5551112222')"
                  style="border: 1px solid #ccc; padding: 8px; line-height: 1em; font-size: 14px;" required></b-form-input>
                </div>
            </div>
            <div class="row mt-4">
              <div class="col-sm-1 mt-2 p-0">
                <strong>{{ $t('presenter') }}</strong>
              </div>
              <div class="col-sm-11">
                <b-form-group>
                  <PresenterField :existingPresenters="form.presenter" @presenter-form-field-update="presenterUpdate" />
                </b-form-group>
              </div>
            </div>
            <button id="save_meeting" data-test="save_meeting" type="submit" class="btn btn-primary mt-4">{{ $t('network.meeting.saveMeeting') }}</button>
          </form>
        </div>
      </div>
    </div>
  </div>
</div>
</template>
<script>
import PresenterField from './presenters/PresenterMeetingField.vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import moment from 'moment-timezone';
export default {
  components: {
    PresenterField,
    DatePicker,
    vSelect,
  },
  props: {
    existingForm: {
      type: Object,
      default: null,
    },
    timezones: {
      type: Array,
      default: null,
    },
    forOccurrence: {
      type: Boolean,
      default: false,
    },
    update: {
      type: Boolean,
      default: false,
    },
    meeting: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      busy: true,
      daysOfWeek: [
        { id: 1, text: 'Sun' },
        { id: 2, text: 'Mon' },
        { id: 3, text: 'Tue' },
        { id: 4, text: 'Wed' },
        { id: 5, text: 'Thu' },
        { id: 6, text: 'Fri' },
        { id: 7, text: 'Sat' },
      ],
      fiveHoursIn15MinutesInterval: [
        { value: '30', text: '30m' },
        { value: '60', text: '1h' },
        { value: '90', text: '1h 30m' },
        { value: '120', text: '2h' },
        { value: '150', text: '2h 30m' },
        { value: '180', text: '3h' },
        { value: '210', text: '3h 30m' },
        { value: '240', text: '4h' },
        { value: '270', text: '4h 30m' },
        { value: '300', text: '5h' },
      ],
      hours: Array.from({ length: 12 }).map((_, i) => i + 0),
      am_pm: 'PM',
      form: null,
      zoom_id: '',
      currentMeetingView: 'internalMeeting',
      meetingPresenters: [],
      selectedMonthlyOption: '',
      isExtended: (this.$route.params.isExtended !== undefined || this.$route.query.isExtended !== undefined) ? true : false,
      isPastOccurrence: this.$route.params.isPastOccurrence ?? false,
    };
  },
  watch: {
    existingForm: {
      handler: function (newVal) {
        this.busy = true;
        if (newVal.recurring && newVal.recurring.frequency == 'Weekly' && newVal.recurring.occurs_on) {
          let occursOn = newVal.recurring.occurs_on.split(',');
          newVal.recurring.occurs_on_array = this.daysOfWeek.filter(day => occursOn.includes('' + day.id));
        }
        this.form = JSON.parse(JSON.stringify(newVal));
        this.am_pm = this.form.start_time ? moment(this.form.start_time, 'hh:mm A').format('A') : 'PM';
        this.selectedMonthlyOption = this.form.recurring && this.form.recurring.occurs_on_week != null ? 'type_two' : 'type_one';
        if (this.isExtended && !this.isPastOccurrence) {
          // by default start date is date from last occurrence and end date is 1 year from start date
          this.form.start_date = this.utcToLocal(this.$route.params.startTime).format('YYYY-MM-DD');
          this.form.start_time = this.utcToLocal(this.$route.params.startTime).format('hh:mm A');
          this.am_pm = moment(this.form.start_time, 'hh:mm A').format('A');
          this.form.recurring.endDate =  moment(this.form.start_date).add(1, 'year').format('YYYY-MM-DD');
        }
        if (this.isPastOccurrence) {
          this.form.start_date = null;
          this.form.start_time = null;
          this.form.recurring.endDate =  null;
        }
        setTimeout(() => {
          this.busy = false;
        }, 1000);
      },
      immediate: true,
      deep: true,
    },
    'form.timezone': function (newVal) {
      this.form.timezone_id = newVal.id;
    },
    '$route.query.isExtendedInAdvance': {
      immediate: true,
      handler(newVal) {
        if (newVal !== undefined) {
          document.body.classList.add('blur-background');
          this.$http.put('/networks/meetings/autoextend/' + this.$route.params.id).then(() => {
            this.$swal.fire({
              icon: 'success',
              title: 'This meeting will be automatically extended after its last occurrence.',
              confirmButtonText: 'OK',
              showCancelButton: false,
              allowOutsideClick: false,
              customClass: {
                confirmButton: 'btn btn-primary',
              },
            }).then((result) => {
              if(result.isConfirmed) {
                document.body.classList.remove('blur-background');
                this.$router.push('/networks/' + this.$route.query.network_slug + '/meetings');
              }
            });
          });
        }
      },
    },
  },
  methods: {
    utcToLocal: function (dt) {
      return moment.utc(dt).local();
    },
    moment(date = null) {
      return moment(date);
    },
    resetWeeklyOccursOn(newVal) {
      this.form.recurring.occurs_on = newVal.map(val => val.id).join(',');
    },
    validateNonRecurringFields() {
      return this.form.start_date !== null && this.form.start_time !== null && (this.form.timezone && this.form.timezone.name.length !== 0);
    },
    presenterUpdate(options) {
      if (Array.isArray(options)) {
        this.meetingPresenters.splice(0);
        options.forEach(op => this.meetingPresenters.push(op));
      }
    },
    validateInputs() {
      if (this.currentMeetingView == 'internalMeeting'){
        if (!this.validateNonRecurringFields() || (this.form.isRecurring && !this.form.recurring.endDate) || this.form.content.length == 0) {
          this.$swal.fire({
            icon: 'error',
            title: 'Oops!',
            text: this.$t('it-seems-that-some-required-fields-are-missing-please-make-sure-to-fill-in-all-the-necessary-information-before-proceeding'),
          });
          return false;
        }
        return true;
      }
    },
    saveMeeting() {
      if (this.currentMeetingView == 'externalMeeting') {
        if (this.meetingPresenters.length > 0) {
          this.form = JSON.parse(JSON.stringify(this.form));
          this.form.presenter.splice(0);
          this.meetingPresenters.forEach(p => this.form.presenter.push(p));
        }
        this.$emit('network-zoom-meeting', (this.zoom_id).split(" ").join(""), this.form);
      } else {
        if (this.validateInputs()) {
          if (this.meetingPresenters.length > 0) {
            this.form = JSON.parse(JSON.stringify(this.form));
            this.form.presenter.splice(0);
            this.meetingPresenters.forEach(p => this.form.presenter.push(p));
          }
          if (this.form.recurring) delete this.form.recurring.occurs_on_array;

          // remove AM/PM from start_time if it exists
          let startTime = this.form.start_time.replace(/\s?(AM|PM)/i, '');
          // define the format string for parsing the input date and time
          let format = 'YYYY-MM-DD hh:mm A';
          // create the moment object with the input and the format
          this.form.start_time = startTime + ' ' + this.am_pm;
          this.form.start = moment(this.form.start_date + ' ' + startTime + ' ' + this.am_pm, format).format('YYYY-MM-DD hh:mm A');console.log(this.form.start);
          this.form.isExtended = this.isExtended;
          this.form.auto_extend = this.form.auto_extend ?? false;
          this.$emit('network-meeting-form-update', this.form);
        }
      }
    },
  },
};
</script>
<style scoped>
.b-time-header {
  display: none;
}
.meetings-page .nav-pills .nav-item .nav-link {
  border-radius: 10px !important;
  margin-right: 10px;
  border: none;
}
.meetings-page .nav-pills .nav-item .nav-link.active {
  background: none;
  font-weight: bolder;
  color: #926dde;
}
.ti-input {
  border-radius: 0.75rem;
}
div.vue-tags-input.w-100 {
  max-width: unset;
}
.mx-datepicker {
  width: 125px;
}
.timeInterval {
  width: 55px;
  height: 30px;
  border-radius: 1rem;
  border-color: rgb(192, 192, 192);
  text-align: center;
}
.card {
  float: unset;
}
body.blur-background > [aria-hidden='true'] {
  transition: 0.1s filter;
  filter: blur(3px);
}
</style>
<style>
.mx-table {
  width: initial !important;
}
</style>